import {
  Module,
  Mutation,
  Action,
  VuexModule,
  getModule
} from "vuex-module-decorators";
import $axios from "@/axios-instance";
import isEmpty from "lodash/isEmpty";

import store, { StoreError } from "../index";
import { UiState, LabelType, User, Response, FloorplanInfo } from "../types";

@Module({ store, dynamic: true, namespaced: true, name: "ui" })
class Ui extends VuexModule implements UiState {
  fetchFailed = false;
  hasUnsavedChange = false;
  annoType = "move";
  snap = true;
  showLabelOverlay = true;
  viewOffset = [0, 0] as [number, number];
  svgMarginRatio = 0.2;
  annotationAlpha = 0.9;
  selectedEdges = null as [{ i: number; j: number }] | null;
  selectedLabelType = "";
  selectedMiscType = "";
  // Annotator Settings
  // ------------------------------
  // nodeの円の大きさ
  circleRadius = 3;
  // edgeの太さ
  strokeWidth = 2;
  // nodeとedgeの色
  shapeColor = "#7957d5";
  // ラベルのラベルの大きさ
  labelScale = 0.8;
  // 各ラベルの色
  labelTypes = [] as LabelType[];
  isLoadingAnnotation = false;
  // Admin Page States
  // ------------------------------
  selectedImageRow = {};
  checkedImageRows = [] as object[];
  username = "";
  projects = [] as object[];
  project = {
    // floorplans assigned to user
    floorplans: [] as { name: string }[],
    // all floorplans in the project
    floorplansAdmin: [] as FloorplanInfo[]
  };
  currentUser = {
    admin: false,
    settings: null,
    ui: {
      selectedFloorplan: "",
      selectedProject: "",
      selectedFloorplanPage: ""
    }
  } as User;
  users = [] as string[];
  images = [] as object[];

  // Mutations
  @Mutation
  setFetchFailed(val: boolean) {
    this.fetchFailed = val;
  }
  @Mutation
  setAnnoType(type: string) {
    this.annoType = type;
  }
  @Mutation
  setLabelType(type: string) {
    this.selectedLabelType = type;
  }
  @Mutation
  setMiscType(name: string) {
    this.selectedMiscType = name;
  }
  @Mutation
  setCircleRadius(r: number) {
    this.circleRadius = r;
  }
  @Mutation
  setStrokeWidth(w: number) {
    this.strokeWidth = w;
  }
  @Mutation
  setShapeColor(c: string) {
    this.shapeColor = c;
  }
  @Mutation
  setLabelScale(val: number) {
    this.labelScale = val;
  }
  @Mutation
  setSelectedImageRow(r: {}) {
    this.selectedImageRow = r;
  }
  @Mutation
  setCheckedImageRows(r: object[]) {
    this.checkedImageRows = r;
  }
  @Mutation
  setUsers(users: string[]) {
    this.users = users;
  }
  @Mutation
  setImages(images: object[]) {
    this.images = images;
  }
  @Mutation
  setUsername(val: string) {
    this.username = val;
  }
  @Mutation
  setCurrentUser(user: User) {
    this.currentUser = user;
  }
  @Mutation
  setProjectFloorplans(val: { name: string }[]) {
    this.project.floorplans = val;
  }
  @Mutation
  setProjectFloorplansAdmin(val: FloorplanInfo[]) {
    this.project.floorplansAdmin = val;
  }
  @Mutation
  setHasUnsavedChange(flag: boolean) {
    this.hasUnsavedChange = flag;
  }
  @Mutation
  setIsLoadingAnnotation(flag: boolean) {
    this.isLoadingAnnotation = flag;
  }
  @Mutation
  setSnap(flag: boolean) {
    this.snap = flag;
  }
  @Mutation
  setShowLabelOverlay(flag: boolean) {
    this.showLabelOverlay = flag;
  }
  @Mutation
  setViewOffset(val: [number, number]) {
    this.viewOffset = val;
  }
  @Mutation
  setSvgMarginRatio(val: number) {
    this.svgMarginRatio = val;
  }
  @Mutation
  setAnnotationAlpha(val: number) {
    this.annotationAlpha = val;
  }
  @Mutation
  setProjects(val: object[]) {
    this.projects = val;
  }
  @Mutation
  setSelectedEdges(val: [{ i: number; j: number }]) {
    this.selectedEdges = val;
  }
  @Mutation
  setLabelTypes(val: LabelType[]) {
    this.labelTypes = val;
  }

  // Getters
  get currentProject() {
    return this.project;
  }
  get selectedImageId() {
    // error handling
    if (isEmpty(this.currentUser) || !this.currentUser.ui) return undefined;

    return this.currentUser.ui.selectedFloorplan;
  }
  get selectedProjectId() {
    if (!this.currentUser.ui) {
      return undefined;
    }
    return this.currentUser.ui.selectedProject;
  }
  get currentUserId() {
    return this.username;
  }
  get isAdmin() {
    return this.currentUser.admin;
  }
  get userSettings() {
    if (this.currentUser.settings) {
      return this.currentUser.settings;
    } else {
      return null;
    }
  }

  get floorplanPage() {
    if (this.currentUser && this.currentUser.ui) {
      return this.currentUser.ui.selectedFloorplanPage;
    }
    return undefined;
  }

  // Actions
  @Action({ rawError: true })
  async signin(req: { username: string; password: string }) {
    try {
      const res = await $axios.post<Response<{ username: string }>>(
        "/signin",
        req
      );
      this.setUsername(res.data.payload.username);
    } catch (err) {
      throw new StoreError("Signin failed");
    }
  }
  @Action
  async signout() {
    await $axios.post("/signout");
    this.setUsername("");
    this.setCurrentUser({
      admin: false,
      settings: null,
      ui: {
        selectedFloorplan: "",
        selectedProject: "",
        selectedFloorplanPage: ""
      }
    });
    this.context.commit("annotations/setAnnotation", {}, { root: true });
  }
  @Action
  async putSettings() {
    await $axios.put(`/users/${this.username}`, {
      settings: {
        circleRadius: this.circleRadius,
        strokeWidth: this.strokeWidth,
        shapeColor: this.shapeColor,
        labelScale: this.labelScale
      }
    });
  }
  @Action
  async fetchUsername() {
    try {
      const res = await $axios.post<Response<{ username: string }>>("/auth");
      this.setUsername(res.data.payload.username);
      return;
    } catch (err) {
      console.error("Authentication Failed");
      throw err;
    }
  }
  @Action({ rawError: true })
  async fetchLabelTypes() {
    // Throw if no project is selected
    if (!this.selectedProjectId) {
      throw new StoreError("Project not selected");
    }
    try {
      const res = await $axios.get<Response<LabelType[]>>(
        `/projects/${this.selectedProjectId}/labelTypes`
      );
      if (!res.data.success) {
        throw new StoreError("Server access failed");
      }
      this.setLabelTypes(res.data.payload);
    } catch {
      throw new StoreError("Server access failed");
    }
  }
  @Action
  async postLabelTypes() {
    await $axios.put(`/projects/${this.selectedProjectId}`, {
      labelTypes: this.labelTypes
    });
  }
  @Action
  async syncSelectedProject(projectName: string) {
    // Throw if argument is empty
    if (!projectName || projectName == "") {
      throw new StoreError("argument projectName is empty");
    }
    // Throw if username is invalid
    if (!this.currentUserId) {
      throw new StoreError("username is not set");
    }
    // Create new ui object
    const newUi = Object.assign({}, this.currentUser.ui, {
      selectedProject: projectName
    });
    // Put new ui object in server
    try {
      await $axios.put(`users/${this.currentUserId}`, {
        ui: newUi
      });
    } catch (err) {
      throw new StoreError("Server access failed");
    }
    // Put new ui object in local state
    this.setCurrentUser(
      Object.assign({}, this.currentUser, {
        ui: newUi
      })
    );
  }
  @Action({ rawError: true })
  async fetchCurrentUser() {
    //
    // Fetching username if its empty
    // ---------------------------------------------------------------
    //
    try {
      if (this.username === "") {
        await this.fetchUsername();
      }
    } catch (err) {
      throw new StoreError("Please Login ");
    }
    //
    // Fetch user info
    // ---------------------------------------------------------------
    //
    try {
      const res = await $axios.get<Response<User>>(`/users/${this.username}`);
      this.setCurrentUser(res.data.payload);
    } catch (error) {
      throw new StoreError("User does not exist");
    }
    const settings = this.userSettings;
    if (settings) {
      if (settings.circleRadius) {
        this.setCircleRadius(settings.circleRadius);
      }
      if (settings.strokeWidth) {
        this.setStrokeWidth(settings.strokeWidth);
      }
      if (settings.shapeColor) {
        this.setShapeColor(settings.shapeColor);
      }
      if (settings.labelScale) {
        this.setLabelScale(settings.labelScale);
      }
    }
    // fetch labelTypes
    await this.fetchLabelTypes();
  }
  @Action
  async unselectFloorplan() {
    const newUi = Object.assign({}, this.currentUser.ui, {
      selectedFloorplan: "",
      selectedFloorplanPage: ""
    });
    await $axios.put(`/users/${this.currentUserId}`, {
      ui: newUi
    });
    this.setCurrentUser(
      Object.assign({}, this.currentUser, {
        ui: newUi
      })
    );
  }
  @Action({ rawError: true })
  async selectFirstImageInProject() {
    //
    // Reset local annotation state
    // ---------------------------------------------------------------
    //
    this.context.dispatch("annotations/resetLocal", null, { root: true });
    //
    // Throw if no project is selected
    // ---------------------------------------------------------------
    //
    if (!this.selectedProjectId) {
      throw new StoreError("Project not selected");
    }
    //
    // Try fetching first page
    // ---------------------------------------------------------------
    //
    // Fetch first page
    const res = await $axios.get<
      Response<{
        docs: { name: string }[];
        totalPages: number;
      }>
    >(
      `/users/${this.currentUserId}/project/${this.selectedProjectId}?page=1&limit=1`
    );
    const data = res.data;
    // Throw on server error
    if (!data || !data.success) {
      throw new StoreError("Server Access Failed");
    }
    // Throw if project is empty
    const totalPages = data.payload.totalPages;
    const docs = data.payload.docs;
    if (totalPages === 0 || docs.length === 0) {
      throw new StoreError("Project is empty");
    }
    //
    // Sync selected page and floorplan name
    // ---------------------------------------------------------------
    //
    // Create new ui object
    const newUi = Object.assign({}, this.currentUser.ui, {
      selectedFloorplan: docs[0].name,
      selectedFloorplanPage: "1"
    });
    try {
      await $axios.put(`users/${this.currentUserId}`, {
        ui: newUi
      });
    } catch (error) {
      throw new StoreError("Server Access Failed");
    }
    this.setCurrentUser(
      Object.assign({}, this.currentUser, {
        ui: newUi
      })
    );
  }
  @Action
  fetchUsers() {
    return new Promise((resolve, reject) => {
      $axios
        .get<Response<string[]>>("/users")
        .then(res => {
          this.setUsers(res.data.payload);
          resolve();
        })
        .catch(e => {
          console.error(`Error at action fetchUsers in ui: ${e}`);
          reject();
        });
    });
  }
  @Action({ rawError: true })
  assignImagesToUsers(payload: { userId: string; imageIds: string[] }) {
    const request = payload.imageIds.map(imageId => ({
      userName: payload.userId,
      floorplanName: imageId
    }));

    return new Promise((resolve, reject) => {
      $axios
        .put("/batch/floorplans/assignedUser", request)
        .then(() => {
          resolve();
        })
        .catch(e => {
          console.error(`Error at action assignImagesToUsers in ui: ${e}`);
          reject();
        });
    });
  }
  @Action
  async fetchProjectList() {
    const res = await $axios.get<Response<object[]>>("/projects");
    this.setProjects(res.data.payload);
  }
  @Action
  async syncSelectedFloorplanPage(nextPage: number) {
    const newUi = Object.assign({}, this.currentUser.ui, {
      selectedFloorplanPage: nextPage
    });
    try {
      await $axios.put(`/users/${this.currentUserId}`, {
        ui: newUi
      });
      this.setCurrentUser(
        Object.assign({}, this.currentUser, {
          ui: newUi
        })
      );
    } catch (error) {
      throw new StoreError("Server access failed");
    }
  }
  /**
   * cycle floorplans back and forth
   * @param {boolean} next - true for cycling forward, false for cycling backwars
   */
  @Action({ rawError: true })
  async cycleFloorplan(next: boolean) {
    //----------------------------------------------------------------
    //
    // Determine current page
    // ---------------------------------------------------------------
    //
    // By setting 0 as the default current page, next page would be 1
    // if cycling next and the last page if cycling back
    let currentPage = 0;
    if (this.floorplanPage) {
      // get current page in int
      const parsed = parseInt(this.floorplanPage);
      // if isNaN, select page 1
      currentPage = isNaN(parsed) ? 0 : parsed;
    }
    //
    // Check if project has any floorplans at all
    // ---------------------------------------------------------------
    //
    // Throw if no project is selected
    if (!this.selectedProjectId) {
      throw new StoreError("No project is selected");
    }
    const res = await $axios.get<
      Response<{
        totalPages: number;
      }>
    >(
      `/users/${this.currentUserId}/project/${this.selectedProjectId}?page=${currentPage}&limit=1`
    );
    const data = res.data;
    if (!data || !data.success) {
      throw new StoreError("Server Access Failed");
    }
    // Throw if project is empty
    const totalPages = data.payload.totalPages;
    // TODO: Not sure if this means anything. Total page is always > 0 even if its empty
    if (totalPages === 0) {
      throw new StoreError("Project is empty");
    }
    //
    // Determine next floorplan name
    // ---------------------------------------------------------------
    //
    let nextPage: number;
    // Select next page if next is true, select previous if false
    if (next) {
      // If currentPage is the last page, go back the first
      nextPage = currentPage + 1 <= totalPages ? currentPage + 1 : 1;
    } else {
      // Select last page if current page is 1
      nextPage = currentPage > 1 ? currentPage - 1 : totalPages;
    }
    // Fetch next page
    const result = await $axios.get<
      Response<{
        docs: { name: string }[];
        totalPages: number;
      }>
    >(
      `/users/${this.currentUserId}/project/${this.selectedProjectId}?page=${nextPage}&limit=1`
    );
    const nextData = result.data;
    // Throw on server error
    if (!nextData || !nextData.success || nextData.payload.docs.length === 0) {
      throw new StoreError("Server Access Failed");
    }
    // TODO: merge two sync functions together
    await this.syncSelectedImage(nextData.payload.docs[0].name);
    await this.syncSelectedFloorplanPage(nextPage);
  }
  @Action
  /**
   * fetch floorplan from server and set in vuex
   * @param {object} param0 - given by vuex
   * @param {*} imageId - name of floorplan to fetch and select
   */
  async syncSelectedImage(imageId: string) {
    // Reset UI
    this.setViewOffset([0, 0]);
    this.setSvgMarginRatio(0.1);

    const userId = this.currentUserId;
    const selectedProject = this.selectedProjectId;
    // fetch annotation to check if it acutually exists and set on local
    await this.context.dispatch("annotations/fetchAnnotation", imageId, {
      root: true
    });
    // change selectedFloorplan and selectedProject on server
    const res = await $axios.put<Response<User>>(`users/${userId}`, {
      ui: {
        selectedFloorplan: imageId,
        selectedProject
      }
    });
    // set on local
    this.setCurrentUser(res.data.payload);
  }
}

export default getModule(Ui);
